'use client';

import { signIn } from 'next-auth/react';
import { SparklesIcon } from 'lucide-react';
import { Checkmark } from './Checkmark';
import { plans } from './pricing-data';
import { PlatformEnum } from '@/server/utils/platform-enum';

export function PricingSection() {
  return (
    <section className="relative z-10 py-24 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-orange-500/20 to-pink-500/20 backdrop-blur-md border border-orange-500/30 rounded-full mb-8">
            <SparklesIcon className="w-5 h-5 text-orange-400" />
            <span className="text-orange-300 font-semibold text-sm">Simple Pricing</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Choose Your{' '}
            <span className="bg-gradient-to-r from-orange-400 to-pink-500 text-transparent bg-clip-text">
              Growth Path
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Start your content engine journey with plans designed for every stage of creator growth
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              className={`group relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 hover:scale-105 ${
                plan.popular
                  ? 'bg-gradient-to-br from-orange-500/20 to-pink-500/20 border-orange-500/30 transform scale-105'
                  : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="text-4xl font-bold text-white mb-2">
                  {plan.name === 'Starter' ? (
                    <>
                      <span className="line-through text-gray-400">{plan.price}</span>
                      <span className="text-lg text-gray-300 font-normal">{plan.period}</span>
                    </>
                  ) : plan.name === 'Studio' ? (
                    <>
                      <span className="text-lg font-normal text-gray-300">Starting at</span>
                      <br />
                      <span>$149</span>
                      <span className="text-lg text-gray-300 font-normal">{plan.period}</span>
                    </>
                  ) : (
                    <>
                      {plan.price}
                      <span className="text-lg text-gray-300 font-normal">{plan.period}</span>
                    </>
                  )}
                </div>
                {plan.trialText && (
                  <div className="text-sm text-orange-400 font-semibold mb-2">{plan.trialText}</div>
                )}
                {plan.afterTrialText && (
                  <div className="text-sm text-gray-400 mb-2">{plan.afterTrialText}</div>
                )}
                <p className="text-gray-300 text-sm">{plan.description}</p>
                <div className="text-xs text-gray-400 mt-1">{plan.followerLimit}</div>
              </div>

              <div className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <Checkmark className="text-white" size="sm" />
                    </div>
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              <div
                onClick={() => signIn(PlatformEnum.TikTok, { callbackUrl: plan.ctaHref })}
                className={`cursor-pointer block text-center w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:from-orange-600 hover:to-pink-600'
                    : plan.name === 'Starter'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-500 hover:to-indigo-500'
                }`}
              >
                {plan.ctaText}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
